import { clsx } from 'clsx';
import { FocusEventHandler, useMemo, CSSProperties, ReactElement } from 'react';
import {
  ActionMeta,
  components,
  DropdownIndicatorProps,
  GroupBase,
  MenuPosition,
  MenuProps,
  OnChangeValue,
  OptionProps,
  OptionsOrGroups,
} from 'react-select';

import { SelectOption } from '~/shared/utils/types';
import SearchIcon from '~/shared/assets/icons/search.svg';

import './react-select.css';

export const SearchIndicator = function <T, isMulti extends boolean = false>(
  props: DropdownIndicatorProps<T, isMulti>
) {
  return (
    <components.DropdownIndicator {...props}>
      <SearchIcon className="w-[24px] h-[24px] stroke-white" />
    </components.DropdownIndicator>
  );
};

export type CommonSelectProps<
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
> = {
  value?: Option | Option[];
  resetOnMenuOpen?: boolean;
  loadOptions?: (
    inputValue: string,
    callback: (options: OptionsOrGroups<Option, Group>) => void
  ) => Promise<OptionsOrGroups<Option, Group>> | void;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  onChange: (newValue: OnChangeValue<Option, IsMulti>, actionMeta: ActionMeta<Option>) => void;
  className?: string;
  placeholder?: string;
  mustTypeFirst?: boolean;
  autoFocus?: boolean;
  customizedOptions?: <T extends SelectOption, isMulti extends boolean = true>(
    props: OptionProps<T, isMulti, GroupBase<T>>
  ) => JSX.Element;
  optionStyles?: CSSProperties;
  hideSelectedOptions?: boolean;
  closeMenuOnSelect?: boolean;
  supportItemLogo?: boolean;
  defaultItemLogoPath?: string;
  disabled?: boolean;
  customMenu?: (props: MenuProps<SelectOption>) => ReactElement;
  menuIsOpen?: boolean;
  key?: string;
};

// eslint-disable-next-line react-refresh/only-export-components
export function useCommonSelectProps<T, isMulti extends boolean = false>(
  props: CommonSelectProps<T, isMulti>
) {
  return useMemo(() => {
    return {
      isDisabled: props.disabled,
      onChange: props.onChange,
      onBlur: props.onBlur,
      value: props.value,
      onMenuOpen: () => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if (props.resetOnMenuOpen) props.onChange(null as any, null as any);
      },
      cacheOptions: true,
      loadOptions: props.loadOptions,
      placeholder: props.placeholder,
      getOptionLabel: (o: SelectOption) => o?.name || '',
      getOptionValue: (o: SelectOption) => o.id,
      classNamePrefix: 'autocomplete',
      defaultOptions: true,
      menuPosition: 'fixed' as MenuPosition,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      closeMenuOnScroll: (e: any) => {
        const isList = !!e.target?.className?.includes?.('autocomplete__menu-list');
        return !isList;
      },
      menuPortalTarget: document.body,

      className: clsx('autocomplete-multi', props.className),
      noOptionsMessage: ({ inputValue }: { inputValue: string }) =>
        !inputValue && props.mustTypeFirst
          ? 'Start typing to autocomplete'
          : !inputValue && !props.mustTypeFirst
            ? 'Loading...'
            : 'No results have been found. Try a different word or phrase.',
    };
  }, [props]);
}

export const Option = function <T, isMulti extends boolean = false>(
  props: OptionProps<T, isMulti>
) {
  return <components.Option {...props} />;
};
